import { Title } from "@solidjs/meta";
import {
  Accessor,
  createEffect,
  createMemo,
  createSignal,
  Match,
  onCleanup,
  onMount,
  Setter,
  Show,
  Suspense,
  Switch,
} from "solid-js";
import ClientOnlyComponent, {
  ClientComponent,
} from "~/client_only_components/client_component";
import { RNR_PAGE_TITLE } from "~/data/config";
import {
  BrandListData,
  HomePageRouteData,
} from "~/server/data/home_page_route_data";
import {
  axisFloorBgDesktop,
  axisFloorBgMobile,
  axisV2DesktopBg,
  ribbonConfetti,
  tdDesktopBg,
  tdFloorBgDesktop,
  tdFloorBgMob,
  tdMobileBg,
} from "~/assets/assets";
import { CategoryGridCard, CategoryStrip } from "../home/category_card";
import occasionsData from "~/data/groups/occasions.json";
import recipientsData from "~/data/groups/recipients.json";
import { zIndexes } from "~/z_indexes";
import { useIsRouting, useSearchParams } from "@solidjs/router";
import {
  BrandsListForDesktop,
  BrandsListForMobile,
  SingleBrandCard,
} from "./brands_card";
import { CoinsBalancePill, GetGiftCardText, Name } from "./components";
import { Spacer } from "~/widgets/spacer";
import { Header } from "../header/header";
import { HubbleBranding } from "~/widgets/branding";
import { Product } from "~/server/types/search";
import { DottedLoader } from "~/widgets/loader";
import { useModal } from "../modal";
import LocalStorageUtil from "~/utils/local_storage";

import useScript from "~/utils/hooks/use_script";
import { OfferConstruct } from "~/server/types/brand";
import {
  ButtonDarkV2,
  CloseModalButton,
} from "../login-rnr/login_rnr_code_component";
import { clientIdConfig, showCoinsPillOnHome } from "~/data/partner_config";
import { LocalStorageKey } from "~/types";

export default function HomeComponentV2(props: {
  routeData: Accessor<HomePageRouteData | undefined>;
  latestCoinsBalance: Accessor<number | undefined>;
  setRefreshLatestBalance: Setter<boolean>;
}) {
  const [showCategoryGrid, setShowCategoryGrid] = createSignal(true);
  const isRouting = useIsRouting();
  const [showCoinsPillOnHeader, setShowCoinsPillOnHeader] = createSignal(false);
  const { openModal } = useModal()!;

  const [queryParams, _setQueryParams] = useSearchParams();
  let maxBrands = parseInt((queryParams.maxBrands as string) ?? "0");

  const allBrands: Accessor<Product[]> = createMemo(() => {
    if (!props.routeData()) return [];

    const brands = props
      .routeData()!
      .brandList.filter((item) => "showAllBrandsTitle" in item)
      .flatMap((item) => item.brands);

    if (maxBrands > 0) {
      return brands.slice(0, maxBrands);
    }

    return brands;
  });

  let categoryObserver: IntersectionObserver;
  let observerCategoryGridRef: HTMLDivElement | undefined = undefined;

  onMount(() => {
    if (props.routeData()?.offerConstruct?.subscriptionRewardDetails) {
      const offerConstruct = props.routeData()?.offerConstruct;
      const userId = props.routeData()?.userProfile?.id;
      const modalShownLocalStorageKey = `${LocalStorageKey.OfferConstructModalShown}${userId}`;
      const modalShown = LocalStorageUtil.getItem<string>(
        modalShownLocalStorageKey
      );

      if (modalShown !== "1") {
        openModal(
          () => (
            <ViewOfferDetails
              offerConstruct={offerConstruct}
              playLottie={true}
            />
          ),
          "md:!min-w-[566px] overflow-hidden !max-w-[566px] !p-0",
          "w-[566px] overflow-hidden"
        );
        LocalStorageUtil.setItem(modalShownLocalStorageKey, "1");
      }
    }

    categoryObserver = new IntersectionObserver((entries) => {
      if (
        entries[0].boundingClientRect.height === 0 &&
        entries[0].boundingClientRect.width === 0
      ) {
        return;
      }
      if (entries[0].isIntersecting) {
        setShowCategoryGrid(true);
      } else {
        setShowCategoryGrid(false);
      }
    });

    onCleanup(() => {
      if (observerCategoryGridRef) {
        categoryObserver.unobserve(observerCategoryGridRef);
      }
    });
  });

  createEffect(() => {
    if (props.routeData() && !isRouting()) {
      if (observerCategoryGridRef) {
        categoryObserver.observe(observerCategoryGridRef);
      }
    }
  });

  function showCategoriesForMobile(): boolean {
    return false;
    // (props.routeData()?.categories ?? false) &&
    // props.routeData()?.categories != null &&
    // brandsCount() >= 9
    //         !config.customizedSDKClients.limitedCategoryContent.includes(
    //           props.routeData()?.clientId!
    //         )
  }

  function showCategoriesForDesktop(): boolean {
    return false;
    // (props.routeData()?.categories ?? false) &&
    // props.routeData()?.categories != null &&
    // brandsCount() >= 9
    // // &&
    // !config.customizedSDKClients.limitedCategoryContent.includes(
    //   props.routeData()?.clientId!
    // )
  }

  function showSearchForMobile(): boolean {
    return false;
  }

  function showSearchForDesktop(): boolean {
    return true;
  }

  const getCoins = createMemo<number>(() => {
    return (
      props.latestCoinsBalance() ??
      props.routeData()?.coins?.totalAvailable ??
      0
    );
  });

  return (
    <Suspense
      fallback={
        <div class="flex flex-col justify-center items-center">
          <DottedLoader color="#999" />
        </div>
      }
    >
      <div class="relative flex flex-col justify-start items-center gap-4 min-h-screen">
        <ClientOnlyComponent component={ClientComponent.ModalHost} />
        <Title>{RNR_PAGE_TITLE}</Title>

        <Header
          isLoggedIn={() => true}
          mode={props.routeData()?.mode ?? "rnr"}
          coins={getCoins}
          authMethod={props.routeData()?.clientConfig?.authMethod}
          showCoins={showCoinsPillOnHeader}
          setRefreshCoins={props.setRefreshLatestBalance}
          clientId={props.routeData()?.clientId}
          showActions={false}
          hideSearchBar={allBrands().length == 1}
          headerImageUrl={props.routeData()?.clientConfig?.clientLogoUrl}
          userOfferConstructStatus={
            props.routeData()?.offerConstruct?.userOfferConstructStatus
          }
        />

        <Show
          when={allBrands() && !isRouting()}
          fallback={<DottedLoader color="#999" />}
        >
          <div
            class="mt-5"
            classList={{
              "sm:mt-[16px]": allBrands().length == 1,
              "sm:mt-[40px]": allBrands().length > 1,
            }}
          ></div>
          <div class="sm:flex flex-col justify-start items-center hidden w-full">
            <DesktopUI />
          </div>
          <div class="flex flex-col justify-start items-center md:hidden w-full">
            <MobileUI />
          </div>

          <Show when={allBrands().length > 1}>
            <div class="mt-[70px]"></div>
            <div class="bottom-0 absolute pb-3 w-full">
              <HubbleBranding class="pt-4 pb-1 w-full" />
              <Show
                when={props.routeData()?.clientId === clientIdConfig.tdClientId}
              >
                <p class="text-center text-medium text-textNormal">
                  This portal is developed by Hubble money for TATA digital
                </p>
              </Show>
            </div>
          </Show>
        </Show>
        <Show when={allBrands().length == 1}>
          <div
            class={`fixed bottom-0 flex w-full ${
              props.routeData()?.clientId === clientIdConfig.axisClientId
                ? "h-[200px] sm:h-[338px]"
                : "h-[325px] sm:h-[338px]"
            } bg-[image:var(--mobile-image-url)] bg-cover bg-center sm:bg-[image:var(--desktop-image-url)]`}
            style={{
              "background-size":
                props.routeData()?.clientId === clientIdConfig.axisClientId
                  ? "100% 100%"
                  : "cover",
              "--mobile-image-url": `url(${props.routeData()?.clientId === clientIdConfig.axisClientId ? axisFloorBgMobile : tdFloorBgMob})`,
              "--desktop-image-url": `url(${props.routeData()?.clientId === clientIdConfig.axisClientId ? axisFloorBgDesktop : tdFloorBgDesktop})`,
            }}
          ></div>
        </Show>
      </div>
    </Suspense>
  );

  function MobileUI() {
    return (
      <>
        <div class="px-3 w-full">
          <div
            class="flex flex-col justify-start items-center bg-cover p-4 rounded-b-3xl w-full"
            style={
              allBrands().length > 1
                ? {
                    "background-image": `url(${tdMobileBg})`,
                  }
                : {}
            }
          >
            <Name
              name={props.routeData()?.name ?? ""}
              clientId={props.routeData()?.clientId!}
            />
            <Spacer height={8} />
            <span class="max-w-[319px] font-jakartaSans font-medium md:font-semibold text-[24px] text-center leading-[32px] md:leading-[36px] tracking-[-0.48px] md:tracking-normal">
              <HeaderText />
            </span>
            <div class="mt-[16px]"></div>
            <Switch>
              <Match
                when={
                  props.routeData() &&
                  showCoinsPillOnHome(props.routeData()!!.clientId)
                }
              >
                <CoinsPill isMobile={true} />
              </Match>
              <Match
                when={
                  props.routeData()?.clientId === clientIdConfig.tdClientId &&
                  props.routeData()?.offerConstruct?.type !== "ONE_TIME_REWARD"
                }
              >
                <ViewOffersPill />
              </Match>
            </Switch>
          </div>
        </div>
        <Switch>
          <Match when={showCategoriesForMobile()}>
            <Spacer height={16} />
            {buildCategoriesForMobile()}
            <Spacer height={32} />
          </Match>
          <Match when={true}>
            <Spacer height={24} />
            <Show when={allBrands().length > 1}>
              <GetGiftCardText
                clientId={props.routeData()?.clientId!}
                brandCount={
                  (props.routeData()?.brandList[0] as BrandListData)?.brands
                    ?.length
                }
              />
              <Spacer height={20} />
            </Show>
          </Match>
        </Switch>
        <Switch>
          <Match when={allBrands().length > 1}>
            {BrandsListForMobile({
              allBrands: allBrands,
              clientId: props.routeData()?.clientId!,
            })}
          </Match>
          <Match when={allBrands().length == 1}>
            {SingleBrandCard({
              brand: () => allBrands()[0],
            })}
          </Match>
        </Switch>
      </>
    );
  }

  function HeaderText() {
    if (
      props.routeData()?.clientId === clientIdConfig.axisClientId ||
      props.routeData()?.clientId === clientIdConfig.bajajClientId
    ) {
      if (allBrands().length === 1) {
        return (
          <>
            {`Generate ${allBrands()[0].voucherProduct.title} gift`} card(s)
            using your coins
          </>
        );
      }
      return <>Shop on your favourite brands with coins</>;
    } else if (props.routeData()?.clientId === clientIdConfig.tdClientId) {
      if (props.routeData()?.offerConstruct?.type === "ONE_TIME_REWARD") {
        if (
          props.routeData()?.offerConstruct?.oneTimeRewardDetails.amountUnit ===
          "PERC"
        ) {
          return (
            <>
              Get instant{" "}
              {props.routeData()?.offerConstruct?.oneTimeRewardDetails.value}%
              off up to ₹
              {
                props.routeData()?.offerConstruct?.oneTimeRewardDetails
                  .maxAmount
              }{" "}
              on your favourite brand
            </>
          );
        } else if (
          props.routeData()?.offerConstruct?.oneTimeRewardDetails.amountUnit ===
          "ABS"
        ) {
          return (
            <>
              Get instant ₹
              {
                props.routeData()?.offerConstruct?.oneTimeRewardDetails
                  .maxAmount
              }{" "}
              off your favourite brand
            </>
          );
        }
      } else if (
        props.routeData()?.offerConstruct?.type === "SUBSCRIPTION_REWARD"
      ) {
        if (
          props.routeData()?.offerConstruct?.subscriptionRewardDetails[0]
            .rewardDetails.amountUnit === "PERC"
        ) {
          const rewardValues =
            props
              .routeData()
              ?.offerConstruct?.subscriptionRewardDetails.map(
                (reward) => reward.rewardDetails.value
              ) || [];
          const [lower, higher] = [...rewardValues].sort((a, b) => a - b);
          const maxAmount = Math.max(
            ...(props
              .routeData()
              ?.offerConstruct?.subscriptionRewardDetails.map(
                (reward) => reward.rewardDetails.maxAmount
              ) || [0])
          );
          return (
            <>
              Get{" "}
              {rewardValues.length === 1 ? `${lower}%` : `${lower}%-${higher}%`}{" "}
              off, up to ₹{maxAmount} every month on your favourite brand
            </>
          );
        } else if (
          props.routeData()?.offerConstruct?.subscriptionRewardDetails[0]
            .rewardDetails.amountUnit === "ABS"
        ) {
          const maxAmount = Math.max(
            ...(props
              .routeData()
              ?.offerConstruct?.subscriptionRewardDetails.map(
                (reward) => reward.rewardDetails.maxAmount
              ) || [0])
          );
          return (
            <>Get up to ₹{maxAmount} off every month on your favourite brand</>
          );
        }
      }
    }
  }

  function DesktopUI() {
    return (
      <>
        <Show when={allBrands().length > 1}>
          <div
            class="top-0 absolute w-full h-[700px]"
            style={`
           background-image: url(${props.routeData()?.clientId === clientIdConfig.axisClientId ? axisV2DesktopBg : tdDesktopBg});
           background-size: 100% 100%;
           `}
          ></div>
        </Show>

        <Name
          name={props.routeData()?.name ?? ""}
          clientId={props.routeData()?.clientId!}
        />
        <Spacer height={8} />

        <span
          class="z-10 font-jakartaSans text-[48px] text-center"
          classList={{
            "max-w-[670px] font-semibold":
              props.routeData()?.clientConfig?.authMethod === "CODE",
            "max-w-[763px] leading-normal tracking-[-0.96px] font-medium":
              props.routeData()?.clientId === clientIdConfig.tdClientId,
          }}
        >
          <HeaderText />
        </span>
        <div class="mt-8"></div>
        <Switch>
          <Match
            when={
              props.routeData() &&
              showCoinsPillOnHome(props.routeData()!!.clientId)
            }
          >
            <CoinsPill isMobile={false} />
          </Match>
          <Match
            when={
              props.routeData()?.clientId === clientIdConfig.tdClientId &&
              props.routeData()?.offerConstruct?.type !== "ONE_TIME_REWARD"
            }
          >
            <ViewOffersPill />
          </Match>
        </Switch>
        <Switch>
          <Match when={showCategoriesForDesktop()}>
            <div class="mt-[49px]"></div>
            {buildCategoriesForDesktop()}
          </Match>
          <Match when={allBrands().length > 1}>
            <div class="mt-[44px]"></div>
            <GetGiftCardText
              clientId={props.routeData()?.clientId!}
              brandCount={
                (props.routeData()?.brandList[0] as BrandListData)?.brands
                  ?.length
              }
            />
          </Match>
        </Switch>
        <div class="mt-8"></div>
        <Switch>
          <Match when={allBrands().length > 1}>
            {BrandsListForDesktop({
              allBrands: allBrands(),
            })}
          </Match>
          <Match when={allBrands().length == 1}>
            {SingleBrandCard({
              brand: () => allBrands()[0],
            })}
          </Match>
        </Switch>
      </>
    );
  }

  function buildCategoriesForMobile() {
    return (
      <>
        <div
          class={`fixed top-20 w-full overflow-y-visible`}
          style={{
            "z-index": zIndexes.categoryStrip,
          }}
        >
          <Show
            when={
              showCategoriesForMobile() &&
              !showCategoryGrid() &&
              props.routeData()?.categories &&
              props.routeData()?.categories != null
            }
          >
            <CategoryStrip
              categories={props.routeData()!.categories.data}
              maxCategoriesOnStrip={Math.min(
                10,
                props.routeData()!.categories.data.length
              )}
              scroll={true}
              border={false}
              occasions={occasionsData.occasions}
              recipients={recipientsData.recipients}
            />
          </Show>
        </div>
        <Show when={showCategoriesForMobile()}>
          <div ref={observerCategoryGridRef} class="px-4 w-full">
            <CategoryGridCard
              categories={props.routeData()?.categories!.data ?? []}
              occasions={occasionsData.occasions}
              recipients={recipientsData.recipients}
            />
          </div>
        </Show>
      </>
    );
  }

  function buildCategoriesForDesktop() {
    return (
      <div
        class={`sticky top-[104px]`}
        style={{
          "z-index": zIndexes.categoryStrip,
        }}
      >
        <CategoryStrip
          categories={props.routeData()!.categories.data}
          maxCategoriesOnStrip={Math.min(
            10,
            props.routeData()!.categories.data.length
          )}
          scroll={false}
          border={true}
          occasions={occasionsData.occasions}
          recipients={recipientsData.recipients}
        />
      </div>
    );
  }

  function ViewOffersPill() {
    const { openModal } = useModal()!;
    return (
      <div
        class="z-10 flex justify-center items-center border-[#313538] hover:border-[#ABABAB] bg-baseLight px-4 py-[9.5px] border rounded-full h-9 text-buttonMedium cursor-pointer"
        onClick={() => {
          openModal(
            () => (
              <ViewOfferDetails
                offerConstruct={props.routeData()?.offerConstruct}
                playLottie={false}
              />
            ),
            "md:!min-w-[566px] overflow-hidden !max-w-[566px] !p-0",
            "w-[566px] overflow-hidden"
          );
        }}
      >
        View offer details
      </div>
    );
  }

  function CoinsPill(pillProps: { isMobile: boolean }) {
    return (
      <CoinsBalancePill
        latestCoinsBalance={getCoins}
        mode="rnr"
        setRefreshLatestBalance={props.setRefreshLatestBalance}
        userName={props.routeData()?.name ?? ""}
        isMobile={pillProps.isMobile}
        startObserve={() => {
          return !!props.routeData() && !isRouting();
        }}
        setShowCoinsPillOnHeader={setShowCoinsPillOnHeader}
      />
    );
  }
}

export function ViewOfferDetails(props: {
  offerConstruct: OfferConstruct | undefined;
  playLottie?: boolean;
}) {
  const { closeModal } = useModal()!;

  let ribbonConfettiElement: HTMLInputElement | undefined;

  const lottiePlayerExists =
    typeof window !== "undefined" &&
    window.customElements &&
    window.customElements.get("lottie-player");

  if (props.playLottie && !lottiePlayerExists) {
    useScript(
      "https://unpkg.com/@lottiefiles/lottie-player@0.3.0/dist/lottie-player.js",
      async () => {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        try {
          // @ts-ignore
          ribbonConfettiElement?.play();
        } catch (e) {
          console.error(e);
        }
      }
    );
  } else if (props.playLottie) {
    setTimeout(() => {
      try {
        // @ts-ignore
        ribbonConfettiElement?.play();
      } catch (e) {
        console.error(e);
      }
    }, 1000);
  }

  function RibbonConfettiLottie() {
    return (
      <lottie-player
        ref={ribbonConfettiElement}
        src={ribbonConfetti}
        autoplay={false}
        loop={false}
      ></lottie-player>
    );
  }

  return (
    <div class="relative flex flex-col darkTheme:border-[#212427] bg-black darkTheme:border rounded-2xl w-full md:w-[548px]">
      <div class="flex flex-row justify-between items-start p-6 w-full">
        <span class="text-basePrimaryDark text-h4 text-nowrap">
          Enjoy monthly savings <br />
          on your favourite brand
        </span>
        <CloseModalButton />
      </div>
      <div class="border-[#333232] mx-6 mb-5 p-2 border rounded-2xl">
        <div
          classList={{
            "grid w-full grid-cols-3 pr-2 md:px-2":
              props.offerConstruct?.subscriptionRewardDetails[0].rewardDetails
                .amountUnit === "PERC",
            "grid w-full grid-cols-2 pr-2 md:px-2":
              props.offerConstruct?.subscriptionRewardDetails[0].rewardDetails
                .amountUnit === "ABS",
          }}
        >
          <p
            class="text-center text-f12Bold text-nowrap text-textNormal md:text-mediumSemiBold"
            classList={{
              "col-span-1": true,
            }}
          >
            Duration
          </p>
          <p
            class="text-center text-f12Bold text-nowrap text-textNormal md:text-mediumSemiBold"
            classList={{
              "col-span-1": true,
            }}
          >
            Discount{" "}
            <Show
              when={
                props.offerConstruct?.subscriptionRewardDetails[0].rewardDetails
                  .amountUnit === "PERC"
              }
            >
              %
            </Show>
          </p>
          <Show
            when={
              props.offerConstruct?.subscriptionRewardDetails[0].rewardDetails
                .amountUnit === "PERC"
            }
          >
            <p class="col-span-1 text-center text-f12Bold text-nowrap text-textNormal md:text-mediumSemiBold">
              Max discount
            </p>
          </Show>
        </div>
        <Show
          when={props.offerConstruct?.subscriptionRewardDetails.length === 2}
        >
          <div
            classList={{
              "mt-2 grid h-[87px] w-full grid-cols-3 items-center rounded-xl bg-[#052514] p-2 md:w-[479px]":
                props.offerConstruct?.subscriptionRewardDetails[0].rewardDetails
                  .amountUnit === "PERC",
              "mt-2 grid h-[87px] w-full grid-cols-2 items-center rounded-xl bg-[#052514] p-2 md:w-[479px]":
                props.offerConstruct?.subscriptionRewardDetails[0].rewardDetails
                  .amountUnit === "ABS",
            }}
          >
            <div class="flex justify-center items-center col-span-1 text-[#24DD7D] text-bold text-nowrap">
              {props.offerConstruct?.subscriptionRewardDetails[1].tenure} months
            </div>
            <div class="flex flex-col justify-center items-center col-span-1 text-nowrap">
              <p class="text-bold text-white">
                <Show
                  when={
                    props.offerConstruct?.subscriptionRewardDetails[0]
                      .rewardDetails.amountUnit === "ABS"
                  }
                >
                  ₹
                </Show>
                {
                  props.offerConstruct?.subscriptionRewardDetails[1]
                    .rewardDetails.value
                }
                <Show
                  when={
                    props.offerConstruct?.subscriptionRewardDetails[0]
                      .rewardDetails.amountUnit === "PERC"
                  }
                >
                  %
                </Show>{" "}
                off
              </p>
              <p class="opacity-[0.6] text-medium text-white">every month</p>
            </div>
            <Show
              when={
                props.offerConstruct?.subscriptionRewardDetails[0].rewardDetails
                  .amountUnit === "PERC"
              }
            >
              <div class="flex flex-col justify-center items-center col-span-1 text-nowrap">
                <p class="text-bold text-white">
                  ₹
                  {
                    props.offerConstruct?.subscriptionRewardDetails[1]
                      .rewardDetails.maxAmount
                  }
                </p>
                <p class="opacity-[0.6] text-medium text-white">every month</p>
              </div>
            </Show>
          </div>
          <div
            classList={{
              "mt-2 grid h-[87px] w-full grid-cols-3 items-center rounded-xl bg-[#07262F] p-2 md:w-[479px]":
                props.offerConstruct?.subscriptionRewardDetails[0].rewardDetails
                  .amountUnit === "PERC",
              "mt-2 grid h-[87px] w-full grid-cols-2 items-center rounded-xl bg-[#07262F] p-2 md:w-[479px]":
                props.offerConstruct?.subscriptionRewardDetails[0].rewardDetails
                  .amountUnit === "ABS",
            }}
          >
            <div class="flex justify-center items-center col-span-1 text-[#4AC4E5] text-bold text-nowrap">
              {props.offerConstruct?.subscriptionRewardDetails[0].tenure} months
            </div>
            <div class="flex flex-col justify-center items-center col-span-1 text-nowrap">
              <p class="text-bold text-white">
                <Show
                  when={
                    props.offerConstruct?.subscriptionRewardDetails[0]
                      .rewardDetails.amountUnit === "ABS"
                  }
                >
                  ₹
                </Show>
                {
                  props.offerConstruct?.subscriptionRewardDetails[0]
                    .rewardDetails.value
                }
                <Show
                  when={
                    props.offerConstruct?.subscriptionRewardDetails[0]
                      .rewardDetails.amountUnit === "PERC"
                  }
                >
                  %
                </Show>{" "}
                off
              </p>
              <p class="opacity-[0.6] text-medium text-white">every month</p>
            </div>
            <Show
              when={
                props.offerConstruct?.subscriptionRewardDetails[0].rewardDetails
                  .amountUnit === "PERC"
              }
            >
              <div class="flex flex-col justify-center items-center col-span-1 text-nowrap">
                <p class="text-bold text-white">
                  ₹
                  {
                    props.offerConstruct?.subscriptionRewardDetails[0]
                      .rewardDetails.maxAmount
                  }
                </p>
                <p class="opacity-[0.6] text-medium text-white">every month</p>
              </div>
            </Show>
          </div>
        </Show>
        <Show
          when={props.offerConstruct?.subscriptionRewardDetails.length === 1}
        >
          <div
            classList={{
              "mt-2 grid h-[87px] w-full grid-cols-3 items-center rounded-xl bg-[#07262F] p-2 md:w-[479px]":
                props.offerConstruct?.subscriptionRewardDetails[0].rewardDetails
                  .amountUnit === "PERC",
              "mt-2 grid h-[87px] w-full grid-cols-2 items-center rounded-xl bg-[#07262F] p-2 md:w-[479px]":
                props.offerConstruct?.subscriptionRewardDetails[0].rewardDetails
                  .amountUnit === "ABS",
            }}
          >
            <div class="flex justify-center items-center col-span-1 text-[#4AC4E5] text-bold text-nowrap">
              {props.offerConstruct?.subscriptionRewardDetails[0].tenure} months
            </div>
            <div class="flex flex-col justify-center items-center col-span-1 text-nowrap">
              <p class="text-bold text-white">
                <Show
                  when={
                    props.offerConstruct?.subscriptionRewardDetails[0]
                      .rewardDetails.amountUnit === "ABS"
                  }
                >
                  ₹
                </Show>
                {
                  props.offerConstruct?.subscriptionRewardDetails[0]
                    .rewardDetails.value
                }
                <Show
                  when={
                    props.offerConstruct?.subscriptionRewardDetails[0]
                      .rewardDetails.amountUnit === "PERC"
                  }
                >
                  %
                </Show>{" "}
                off
              </p>
              <p class="opacity-[0.6] text-medium text-white">every month</p>
            </div>
            <Show
              when={
                props.offerConstruct?.subscriptionRewardDetails[0].rewardDetails
                  .amountUnit === "PERC"
              }
            >
              <div class="flex flex-col justify-center items-center col-span-1 text-nowrap">
                <p class="text-bold text-white">
                  ₹
                  {
                    props.offerConstruct?.subscriptionRewardDetails[0]
                      .rewardDetails.maxAmount
                  }
                </p>
                <p class="opacity-[0.6] text-medium text-white">every month</p>
              </div>
            </Show>
          </div>
        </Show>
      </div>
      <div class="darkTheme:border-[#212427] p-6 darkTheme:border-t">
        <ButtonDarkV2 isLoading={false} onClick={() => closeModal()}>
          <p>Got it</p>
        </ButtonDarkV2>
      </div>
      <div class="top-0 right-0 left-0 absolute flex pointer-events-none scale-150">
        <RibbonConfettiLottie />
      </div>
    </div>
  );
}
